import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { Div } from 'glamorous';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  SportCenters,
  SportCenter,
  Playground,
  Playgrounds,
  Bookings,
  StandaloneBookings,
  Users,
  User,
  Login,
  Reports,
  ChatReports,
  SectionsDashboard,
  OrganizationStaff,
  Help,
  OperatorHome,
} from './containers';
import Notifications from './containers/Notifications/Notifications';
import ModeratorRegistration from './containers/ModeratorRegistration/ModeratorRegistration';
import ConfirmEmail from './containers/ConfirmEmail/ConfirmEmail';
import AdminLayout from './components/AdminLayout';
import Logout from './components/Logout';
import ModeratorWelcomeBanner from './components/ModeratorWelcomeBanner/ModeratorWelcomeBanner';
import { initApp } from './ducks';
import { refreshAuth } from './ducks/auth';
import api from './lib/api';

class App extends Component {
  static propTypes = {
    refreshAuth: PropTypes.func.isRequired,
    isAuthorized: PropTypes.bool,
    auth: PropTypes.object,
    initApp: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isAuthorized: false,
    auth: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
    };
  }
  async componentDidMount() {
    // Bridge для OneSignal init в public/index.html (subscription id → /api/users/device)
    window.sendOneSignalUserId = (userId) => {
      if (!userId) return;
      api.setDeviceUser(userId).catch((e) => {
        window.console.error('OneSignal setDeviceUser:', e);
      });
    };

    try {
      const authResult = await this.props.refreshAuth();
      await this.initApp(authResult?.payload?.role ?? this.props.auth?.role);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Ошибка авторизации/инициализации:', err && err.message);
      if (err.message && err.message.includes('подтвердить email')) {
        window.location.pathname = '/login';
      }
    } finally {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ isLoading: false });
    }
    this.sendDeviceId();
    // SDK грузится deferred — повторная попытка после готовности OneSignal
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(() => {
      this.sendDeviceId();
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Если пользователь залогинился или перелогинился, устанавливаем isLoading
    // Это безопасная замена componentWillReceiveProps
    return null; // Не изменяем state здесь, делаем это в componentDidUpdate
  }

  async componentDidUpdate(prevProps) {
    // Пользователь залогинился с логинки и сейчас будут загружаться все данные
    // или изменилось userId - пользователь перелогинился
    if (
      (this.props.isAuthorized && prevProps.isAuthorized === undefined) ||
      (this.props.auth && prevProps.auth && this.props.auth.userId !== prevProps.auth.userId)
    ) {
      this.setState({ isLoading: true });
      try {
        await this.initApp(this.props.auth?.role);
        this.sendDeviceId();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Ошибка инициализации после логина:', err);
      } finally {
        this.setState({ isLoading: false });
      }
    }
  }

  async initApp(role) {
    await this.props.initApp(role);
  }

  // eslint-disable-next-line class-methods-use-this
  async sendDeviceId() {
    try {
      if (window.location.host !== 'admin.sportsground.ru') return;
      const OneSignal = window.OneSignalInstance || window.OneSignal;
      if (!OneSignal) return;

      let userId = null;
      // OneSignal Web SDK v16
      if (OneSignal.User && OneSignal.User.PushSubscription) {
        userId = OneSignal.User.PushSubscription.id;
      }
      // Legacy fallback
      if (!userId && typeof OneSignal.getUserId === 'function') {
        userId = await OneSignal.getUserId();
      }
      if (userId) {
        await api.setDeviceUser(userId);
      }
    } catch (e) {
      window.console.error('OneSignal sendDeviceId:', e);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  renderAdminLayout() {
    return (
      <AdminLayout userRole="administrator">
        <Div style={styles.content}>
          <ModeratorWelcomeBanner />
        </Div>
      </AdminLayout>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderModeratorLayout() {
    return (
      <AdminLayout userRole="moderator">
        <Div style={styles.content}>
          <ModeratorWelcomeBanner />
        </Div>
      </AdminLayout>
    );
  }

  render() {
    const { isAuthorized, auth } = this.props;
    const isAdmin = isAuthorized && auth && auth.role === 'administrator';
    const isModerator = isAuthorized && auth && auth.role === 'moderator';

    return (
      <Router>
        {this.state.isLoading ? (
          <Div style={{ ...styles.app, ...styles.loading }}>
            <CircularProgress size={36} />
            <Div style={styles.loadingText}>Загрузка…</Div>
          </Div>
        ) : (
          <Div style={styles.app}>
            <Switch>
              <Route path="/login" exact render={(props) => <Login {...props} />} />
              <Route path="/register" exact render={(props) => <ModeratorRegistration {...props} />} />
              <Route path="/confirm-email" exact render={(props) => <ConfirmEmail {...props} />} />
              <Route path="/logout" exact render={(props) => <Logout {...props} />} />
              
              {/* Admin routes */}
              <Route path="/" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin) {
                  return (
                    <AdminLayout userRole="administrator">
                      <Div style={styles.content}>
                        <SportCenters {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                if (isModerator) {
                  return (
                    <AdminLayout userRole="moderator">
                      <Div style={styles.content}>
                        <ModeratorWelcomeBanner />
                        <OperatorHome {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/centers" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin) {
                  return (
                    <AdminLayout userRole="administrator">
                      <Div style={styles.content}>
                        <SportCenters {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                if (isModerator) {
                  return (
                    <AdminLayout userRole="moderator">
                      <Div style={styles.content}>
                        <ModeratorWelcomeBanner />
                        <SportCenters {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/new-center" render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <SportCenter {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/center/:centerId/sections/:view?" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  const { centerId, view } = routeProps.match.params;
                  const viewPart = view ? `/${view}` : '';
                  return (
                    <Redirect
                      to={`/sections${viewPart}?centerId=${encodeURIComponent(String(centerId))}`}
                    />
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/sections/:view?" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <SectionsDashboard {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/organization/staff" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <OrganizationStaff {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/center/:id" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <SportCenter {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/playgrounds" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin) {
                  return (
                    <AdminLayout userRole="administrator">
                      <Div style={styles.content}>
                        <Playgrounds {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                if (isModerator) {
                  return (
                    <AdminLayout userRole="moderator">
                      <Div style={styles.content}>
                        <ModeratorWelcomeBanner />
                        <Playgrounds {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/new-playground" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <Playground {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/playground/:id" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <Playground {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/bookings" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <Bookings {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/standalone-bookings" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <StandaloneBookings {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/notifications" exact render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <Notifications {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              <Route path="/users" exact render={(routeProps) => {
                if (!isAuthorized || !isAdmin) return <Redirect to="/login" />;
                return (
                  <AdminLayout userRole="administrator">
                    <Div style={styles.content}>
                      <Users {...routeProps} />
                    </Div>
                  </AdminLayout>
                );
              }} />
              <Route path="/user/:id" exact render={(routeProps) => {
                if (!isAuthorized || !isAdmin) return <Redirect to="/login" />;
                return (
                  <AdminLayout userRole="administrator">
                    <Div style={styles.content}>
                      <User {...routeProps} />
                    </Div>
                  </AdminLayout>
                );
              }} />
              <Route path="/new-user" exact render={(routeProps) => {
                if (!isAuthorized || !isAdmin) return <Redirect to="/login" />;
                return (
                  <AdminLayout userRole="administrator">
                    <Div style={styles.content}>
                      <User {...routeProps} />
                    </Div>
                  </AdminLayout>
                );
              }} />
              <Route path="/reports" exact render={(routeProps) => {
                if (!isAuthorized || (!isAdmin && !isModerator)) return <Redirect to="/login" />;
                return (
                  <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                    <Div style={styles.content}>
                      <Reports {...routeProps} />
                    </Div>
                  </AdminLayout>
                );
              }} />
              <Route path="/chat-reports" exact render={(routeProps) => {
                if (!isAuthorized || !isAdmin) return <Redirect to="/login" />;
                return (
                  <AdminLayout userRole="administrator">
                    <Div style={styles.content}>
                      <ChatReports {...routeProps} />
                    </Div>
                  </AdminLayout>
                );
              }} />
              <Route path="/help/:articleId?" render={(routeProps) => {
                if (!isAuthorized) return <Redirect to="/login" />;
                if (isAdmin || isModerator) {
                  return (
                    <AdminLayout userRole={isAdmin ? 'administrator' : 'moderator'}>
                      <Div style={styles.content}>
                        {isModerator && <ModeratorWelcomeBanner />}
                        <Help {...routeProps} />
                      </Div>
                    </AdminLayout>
                  );
                }
                return <Redirect to="/login" />;
              }} />
              
              {/* Redirect to login if route not matched */}
              <Redirect to="/login" />
            </Switch>
          </Div>
        )}
      </Router>
    );
  }
}

const styles = {
  app: {
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    boxSizing: 'border-box',
  },
  loading: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  loadingText: {
    marginTop: 12,
    color: '#666',
    fontSize: 14,
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 24,
    paddingBottom: 24,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
};

export default connect(state => ({ isAuthorized: state.auth.isAuthorized, auth: state.auth }), {
  initApp,
  refreshAuth,
})(App);
